import React, { type FunctionComponentWithRef, useMemo, useState } from "react";
import type { ChildrenFnProps } from "react-photoswipe-gallery/dist/types";
import { findFirstNonEmptyString } from "scmp-sdk";
import "swiper/css";
import { Navigation } from "swiper/modules";
import { SwiperSlide } from "swiper/react";

import type { ResponsiveVariants } from "shared/lib/styles";
import { notEmpty } from "shared/lib/utils";

import type { Props as BaseImageProps } from "scmp-app/components/common/base-image";
import { BaseImage } from "scmp-app/components/common/base-image";
import { useResponsiveImage } from "scmp-app/components/common/base-image/hooks";
import { Spacer } from "scmp-app/components/common/spacer";
import { CarouselControls } from "scmp-app/components/content/content-cover-image/carousel-controls";
import { useCarousel } from "scmp-app/components/content/content-cover-image/hooks";
import { StyledSwiper } from "scmp-app/components/content/content-cover-image/styles";
import { GalleryItem } from "scmp-app/components/image-gallery/gallery-item";

import {
  ArticleImageContainer,
  DescriptionContainer,
  ImageContainer,
  StyledIconClose,
  StyledIconTooltip,
  StyledImage,
  type StyledProps,
} from "./styles";

export type CarouselSlide = {
  responsiveVariant: ResponsiveVariants<{
    aspectRatio: string;
    url: string;
    width: number;
  }>;
  title?: null | string;
  url: string;
};

export type Props = Partial<PropsFromStyledProps<StyledProps>> & {
  carouselSlides?: CarouselSlide[];
  className?: string;
  imageProps: BaseImageProps;
  skipGallery?: boolean;
  title?: string;
  toggleTitle?: boolean;
};

export const ArticleImage: FunctionComponentWithRef<Props> = ({
  carouselSlides,
  className,
  imageProps,
  ref,
  responsiveDescriptionVariants = { desktopUp: "show", mobileUp: "show", tabletUp: "show" },
  responsiveImageVariants = { desktopUp: "normal", mobileUp: "normal", tabletUp: "normal" },
  skipGallery = false,
  title,
  toggleTitle = false,
}) => {
  const [toggle, setToggle] = useState(false);

  const hasCarousel = (carouselSlides?.length ?? 0) > 1;

  const {
    activeIndex,
    handleDotClick,
    handleNext,
    handlePrevious,
    handleSlideChange,
    swiperReference,
  } = useCarousel();

  const activeTitle = hasCarousel ? (carouselSlides?.[activeIndex]?.title ?? "") : (title ?? "");

  const isShowDescription = useMemo(
    () => (toggleTitle ? toggle : notEmpty(activeTitle)),
    [activeTitle, toggle, toggleTitle],
  );

  const { bindResponsiveAspectRatio } = useResponsiveImage(
    hasCarousel && carouselSlides?.[0]
      ? carouselSlides[0].responsiveVariant
      : (imageProps.responsiveVariant ?? {
          desktopUp: { aspectRatio: "auto", url: "", width: 0 },
          mobileUp: { aspectRatio: "auto", url: "", width: 0 },
          tabletUp: { aspectRatio: "auto", url: "", width: 0 },
        }),
  );

  const handleRenderImage = ({ open, ref }: ChildrenFnProps<HTMLElement>) => {
    if (!skipGallery)
      return (
        <ImageContainer onClick={open}>
          <StyledImage
            $clickable
            ref={ref}
            {...imageProps}
            alt={findFirstNonEmptyString(imageProps.alt, title) || undefined}
          />
        </ImageContainer>
      );

    return (
      <ImageContainer>
        <StyledImage
          $clickable={false}
          {...imageProps}
          alt={findFirstNonEmptyString(imageProps.alt, title) || undefined}
        />
      </ImageContainer>
    );
  };

  const handleRenderCarousel = () => {
    if (!carouselSlides) return null;

    return (
      <ImageContainer>
        <StyledSwiper
          modules={[Navigation]}
          onSlideChange={() => {
            handleSlideChange();
            setToggle(false);
          }}
          ref={swiperReference}
          slidesPerView={1}
          {...bindResponsiveAspectRatio}
        >
          {carouselSlides.map((slide, index) => (
            <SwiperSlide key={slide.url ?? index}>
              <BaseImage
                $objectFit="cover"
                alt={findFirstNonEmptyString(slide.title, title) ?? undefined}
                lazyload={index !== 0}
                responsiveVariant={slide.responsiveVariant}
                src={slide.url}
                {...bindResponsiveAspectRatio}
              />
            </SwiperSlide>
          ))}
        </StyledSwiper>

        {notEmpty(activeTitle) && toggleTitle && !toggle && (
          <StyledIconTooltip onClick={() => setToggle(true)} />
        )}

        {isShowDescription && toggleTitle && (
          <DescriptionContainer>
            {activeTitle}
            <Spacer />
            {toggle && <StyledIconClose onClick={() => setToggle(false)} />}
          </DescriptionContainer>
        )}
      </ImageContainer>
    );
  };

  return (
    <GalleryItem
      src={hasCarousel ? (carouselSlides?.[0]?.url ?? "") : (imageProps.src ?? "")}
      title={activeTitle}
    >
      {galleryProps => (
        <ArticleImageContainer
          $responsiveDescriptionVariants={responsiveDescriptionVariants}
          $responsiveImageVariants={responsiveImageVariants}
          $toggleTitle={toggleTitle}
          className={className}
          ref={ref}
        >
          {hasCarousel ? handleRenderCarousel() : handleRenderImage(galleryProps)}

          {!hasCarousel && notEmpty(activeTitle) && toggleTitle && !toggle && (
            <StyledIconTooltip onClick={() => setToggle(true)} />
          )}

          {(!hasCarousel || !toggleTitle) && isShowDescription && (
            <DescriptionContainer>
              {activeTitle}
              <Spacer />
              {toggle && <StyledIconClose onClick={() => setToggle(false)} />}
            </DescriptionContainer>
          )}

          {hasCarousel && carouselSlides && (
            <CarouselControls
              activeIndex={activeIndex}
              onDotClick={handleDotClick}
              onNext={handleNext}
              onPrevious={handlePrevious}
              totalSlides={carouselSlides.length}
            />
          )}
        </ArticleImageContainer>
      )}
    </GalleryItem>
  );
};

ArticleImage.displayName = "ArticleImage";
